import { db } from "../firebase/config";
import {
  collection,
  doc,
  getDoc,
  setDoc,
  addDoc,
  getDocs,
  query,
  where,
  updateDoc,
  writeBatch,
  serverTimestamp,
} from "firebase/firestore";
import { calculateEmployeeAttendanceAndLop } from "../utils/attendanceCalculation";

// Default settings if none exist in DB
export const defaultPayrollSettings = {
  fullTimeSalary: 30000,
  fresherStipend: 8000,
  experiencedStipend: 15000,
  deferredStipend: 8000,
  pfEmployee: 12,
  pfEmployer: 12,
  overtimeRate: 100,
  bonus: 0,
};

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export function parseMonthNumber(m) {
  if (!m) return new Date().getMonth() + 1;
  if (typeof m === "number") return m;
  const idx = MONTH_NAMES.findIndex((name) => name.toLowerCase() === m.toString().toLowerCase());
  if (idx !== -1) return idx + 1;
  const num = parseInt(m, 10);
  return !isNaN(num) && num >= 1 && num <= 12 ? num : new Date().getMonth() + 1;
}

export function parseMonthName(m) {
  const num = parseMonthNumber(m);
  return MONTH_NAMES[num - 1];
}

function toMillis(ts) {
  if (!ts) return 0;
  if (typeof ts.toMillis === "function") return ts.toMillis();
  if (typeof ts.seconds === "number") return ts.seconds * 1000;
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
}

function latestStructuresByEmployee(structures) {
  const map = new Map();
  for (const s of structures) {
    const existing = map.get(s.employeeId);
    if (!existing || toMillis(s.createdAt) >= toMillis(existing.createdAt)) {
      map.set(s.employeeId, s);
    }
  }
  return map;
}

// Get payroll settings
export const getPayrollSettings = async () => {
  try {
    const docRef = doc(db, "settings", "payroll");
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { ...defaultPayrollSettings, ...docSnap.data() };
    }
    await setDoc(docRef, defaultPayrollSettings);
    return defaultPayrollSettings;
  } catch (error) {
    console.error("Error fetching payroll settings:", error);
    return defaultPayrollSettings;
  }
};

/**
 * Recalculate and update all generated/draft payroll records across the system
 * using the provided payroll settings, live bank details, and monthly attendance.
 */
export const recalculateAllGeneratedPayrolls = async (settings, targetMonth = null, targetYear = null) => {
  const currentSettings = settings || (await getPayrollSettings());
  let updatedCount = 0;

  try {
    // 1. Fetch related collections in parallel
    const [usersSnap, structuresSnap, attendanceSnap, leavesSnap, pendingSnap, payrollsSnap] = await Promise.all([
      getDocs(collection(db, "users")),
      getDocs(collection(db, "salaryStructures")),
      getDocs(collection(db, "attendance")),
      getDocs(collection(db, "leaveRequests")),
      getDocs(collection(db, "pendingPayments")),
      getDocs(collection(db, "payrolls")),
    ]);

    const users = usersSnap.docs.map((d) => ({ id: d.id, uid: d.id, ...d.data() }));
    const structures = structuresSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
    const attendanceRecords = attendanceSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
    const leaveRequests = leavesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
    const latestByEmployee = latestStructuresByEmployee(structures);

    // Fetch bank_details subcollection for all users
    const bankDetailsSnaps = await Promise.all(
      users.map(async (u) => {
        try {
          const bSnap = await getDoc(doc(db, "users", u.uid, "bank_details", "main"));
          return { uid: u.uid, data: bSnap.exists() ? bSnap.data() : null };
        } catch {
          return { uid: u.uid, data: null };
        }
      })
    );
    const bankMap = new Map(bankDetailsSnaps.map((b) => [b.uid, b.data]));

    // 2. Process pendingPayments collection (Finance Module)
    const pendingToUpdate = [];
    pendingSnap.docs.forEach((d) => {
      const p = { id: d.id, ...d.data() };
      const status = (p.status || "").toString();

      // Only update unfinalized / draft payslips (leave Paid and Shared payslips locked)
      if (status === "Paid" || status === "Shared") {
        return;
      }

      if (targetMonth && parseMonthName(p.month) !== parseMonthName(targetMonth)) {
        return;
      }
      if (targetYear && Number(p.year) !== Number(targetYear)) {
        return;
      }

      const emp = users.find(
        (u) => u.uid === p.employeeId || u.id === p.employeeId || (p.employeeId && u.employeeId === p.employeeId)
      );
      const structure = latestByEmployee.get(p.employeeId) || (emp ? latestByEmployee.get(emp.uid) : null);

      const empCategory = (
        emp?.payrollCategory || emp?.employmentType || emp?.category || p.category || "full-time"
      ).toString().toLowerCase();

      const isUnpaid = empCategory === "unpaid-intern" || empCategory === "unpaid";
      const isDeferred = empCategory === "deferred-intern" || empCategory === "deferred";

      let basic = 0;
      let hra = Number(p.hra ?? 0);
      let specialAllowance = Number(p.specialAllowance ?? 0);
      let travelAllowance = Number(p.travelAllowance ?? 0);
      let bonusIncentive = Number(p.bonusIncentive ?? 0);
      let otherAllowance = Number(p.otherAllowance ?? 0);
      let pf = 0;
      let esi = Number(p.esi ?? 0);
      let professionalTax = Number(p.professionalTax ?? 0);
      let incomeTax = Number(p.incomeTax ?? 0);
      let otherDeductions = Number(p.otherDeductions ?? 0);

      if (structure) {
        basic = Number(structure.basic ?? p.basic ?? 0);
        hra = Number(structure.hra ?? p.hra ?? 0);
        specialAllowance = Number(structure.specialAllowance ?? p.specialAllowance ?? 0);
        travelAllowance = Number(structure.travelAllowance ?? p.travelAllowance ?? 0);
        bonusIncentive = Number(structure.bonusIncentive ?? p.bonusIncentive ?? currentSettings.bonus ?? 0);
        otherAllowance = Number(structure.otherAllowance ?? p.otherAllowance ?? 0);
        esi = Number(structure.esi ?? p.esi ?? 0);
        professionalTax = Number(structure.professionalTax ?? p.professionalTax ?? 0);
        incomeTax = Number(structure.incomeTax ?? p.incomeTax ?? 0);
        otherDeductions = Number(structure.otherDeductions ?? p.otherDeductions ?? 0);

        if (isUnpaid || isDeferred || empCategory.includes("intern")) {
          pf = 0;
        } else {
          pf = Math.round((basic * Number(currentSettings.pfEmployee || 12)) / 100);
        }
      } else {
        if (empCategory === "paid-intern" || empCategory === "fresher-intern") {
          basic = Number(emp?.stipend || emp?.salary || currentSettings.fresherStipend || 8000);
          pf = 0;
        } else if (empCategory === "paid-intern-experienced" || empCategory === "experienced-intern") {
          basic = Number(emp?.stipend || emp?.salary || currentSettings.experiencedStipend || 15000);
          pf = 0;
        } else if (isUnpaid) {
          basic = 0;
          pf = 0;
        } else if (isDeferred) {
          basic = Number(emp?.stipend || emp?.salary || currentSettings.deferredStipend || 8000);
          pf = 0;
        } else {
          // Full-time employee
          basic = Number(emp?.fullTimeSalary || emp?.baseSalary || emp?.salary || currentSettings.fullTimeSalary || 30000);
          const pfRate = Number(currentSettings.pfEmployee || 12);
          pf = Math.round((basic * pfRate) / 100);
        }
        bonusIncentive = Number(emp?.bonus || currentSettings.bonus || 0);
      }

      const rowGross = basic + hra + specialAllowance + travelAllowance + bonusIncentive + otherAllowance;
      const salaryDays = 30;
      const monthNum = parseMonthNumber(p.month);
      const yearNum = Number(p.year || new Date().getFullYear());

      const attInfo = calculateEmployeeAttendanceAndLop({
        employeeId: p.employeeId || emp?.uid,
        candidateIds: [emp?.uid, emp?.id, emp?.employeeId, emp?.customId, p.employeeId, p.customEmployeeId],
        month: monthNum,
        year: yearNum,
        monthlyGross: rowGross,
        salaryDays,
        attendanceList: attendanceRecords,
        leavesList: leaveRequests,
      });

      const presentDays = attInfo.presentDays;
      const leaveDays = attInfo.paidLeaveDays;
      const absentDays = attInfo.absentDays;
      const halfDayCount = attInfo.halfDayCount;
      const perDaySalary = attInfo.perDaySalary;
      const fullDayLopDeduction = attInfo.fullDayLopDeduction;
      const halfDayDeduction = attInfo.halfDayDeduction;
      const attendanceLopDeduction = attInfo.totalAttendanceLopDeduction;
      const workedDays = attInfo.workedDays;

      const rowDeductions = pf + esi + professionalTax + incomeTax + otherDeductions + attendanceLopDeduction;
      const rowNet = isUnpaid || isDeferred ? 0 : Math.max(0, rowGross - rowDeductions);

      // Extract personal details from employee profile & subcollections
      const employeeName = emp?.displayName || emp?.name || p.employeeName || "Employee";
      const customEmployeeId = emp?.employeeId || emp?.customId || emp?.empId || p.customEmployeeId || p.employeeId;
      const designation = emp?.designation || emp?.role || p.designation || "Staff";
      const department = emp?.department || emp?.dept || p.department || "Engineering";
      const joiningDate = emp?.joiningDate || emp?.dateOfJoining || emp?.internshipStartDate || p.joiningDate || null;
      
      const bankData = (emp?.uid && bankMap.get(emp.uid)) || (p.employeeId && bankMap.get(p.employeeId)) || {};
      const bankAccountNo = bankData.accountNumber || bankData.bankAccountNo || emp?.accountNumber || emp?.bankAccountNo || emp?.bankAccountNumber || p.bankAccountNo || "NA";
      const bankName = bankData.bankName || emp?.bankName || p.bankName || "NA";
      const ifscCode = bankData.ifscCode || emp?.ifscCode || p.ifscCode || "NA";
      const pfNumber = bankData.pfNumber || bankData.uan || emp?.pfNumber || emp?.pfNo || emp?.uan || p.pfNumber || "NA";

      pendingToUpdate.push({
        id: p.id,
        data: {
          employeeName,
          customEmployeeId,
          designation,
          department,
          joiningDate,
          bankAccountNo,
          bankName,
          ifscCode,
          pfNumber,
          basic,
          hra,
          specialAllowance,
          travelAllowance,
          bonusIncentive,
          otherAllowance,
          pf,
          esi,
          professionalTax,
          incomeTax,
          absentDays,
          halfDayCount,
          perDaySalary,
          fullDayLopDeduction,
          halfDayDeduction,
          attendanceLopDeduction,
          otherDeductions,
          gross: rowGross,
          deductions: rowDeductions,
          net: rowNet,
          presentDays,
          leaveDays,
          workedDays: p.workedDays ?? workedDays,
          updatedAt: serverTimestamp(),
        },
      });
    });

    const CHUNK_SIZE = 450;
    for (let i = 0; i < pendingToUpdate.length; i += CHUNK_SIZE) {
      const chunk = pendingToUpdate.slice(i, i + CHUNK_SIZE);
      const batch = writeBatch(db);
      chunk.forEach((item) => {
        batch.update(doc(db, "pendingPayments", item.id), item.data);
      });
      await batch.commit();
      updatedCount += chunk.length;
    }

    // 3. Process payrolls collection (Admin/HR Module)
    const payrollsToUpdate = [];
    payrollsSnap.docs.forEach((d) => {
      const r = { id: d.id, ...d.data() };
      const status = (r.status || "").toString();

      if (status === "Paid" || status === "Shared") {
        return;
      }

      if (targetMonth && parseMonthName(r.month) !== parseMonthName(targetMonth)) {
        return;
      }
      if (targetYear && Number(r.year) !== Number(targetYear)) {
        return;
      }

      const user = users.find(
        (u) => u.uid === r.userId || u.id === r.userId || (r.employeeId && u.employeeId === r.employeeId)
      );
      const structure = latestByEmployee.get(r.userId) || (user ? latestByEmployee.get(user.uid) : null);

      let category = "Full-Time Employee";
      let baseAmount = Number(
        structure?.basic || user?.fullTimeSalary || user?.baseSalary || user?.salary || currentSettings.fullTimeSalary || 30000
      );
      let isEligibleForPF = true;
      let isDeferred = false;
      let isUnpaid = false;

      const empType = (
        user?.payrollCategory || user?.employmentType || user?.category || r.category || "full-time"
      ).toString().toLowerCase();

      if (empType === "paid-intern" || empType === "fresher-intern") {
        category = "Paid Intern - Fresher";
        baseAmount = Number(structure?.basic || user?.stipend || user?.salary || currentSettings.fresherStipend || 8000);
        isEligibleForPF = false;
      } else if (empType === "paid-intern-experienced" || empType === "experienced-intern") {
        category = "Paid Intern - Experienced";
        baseAmount = Number(structure?.basic || user?.stipend || user?.salary || currentSettings.experiencedStipend || 15000);
        isEligibleForPF = false;
      } else if (empType === "unpaid-intern" || empType === "unpaid") {
        category = "Unpaid Intern";
        baseAmount = 0;
        isEligibleForPF = false;
        isUnpaid = true;
      } else if (empType === "deferred-intern" || empType === "deferred") {
        category = "Deferred Stipend Intern";
        baseAmount = Number(structure?.basic || user?.stipend || user?.salary || currentSettings.deferredStipend || 8000);
        isEligibleForPF = false;
        isDeferred = true;
      }

      const grossEarnings = baseAmount;
      const salaryDays = 30;
      const mNum = parseMonthNumber(r.month);
      const yNum = Number(r.year || new Date().getFullYear());

      const attInfo = calculateEmployeeAttendanceAndLop({
        employeeId: r.userId || user?.uid,
        candidateIds: [user?.uid, user?.id, user?.employeeId, r.userId, r.employeeId],
        month: mNum,
        year: yNum,
        monthlyGross: grossEarnings,
        salaryDays,
        attendanceList: attendanceRecords,
        leavesList: leaveRequests,
      });

      const presentDays = attInfo.presentDays;
      const leaveDays = attInfo.paidLeaveDays;
      const absentDays = attInfo.absentDays;
      const halfDayCount = attInfo.halfDayCount;
      const perDaySalary = attInfo.perDaySalary;
      const fullDayLopDeduction = attInfo.fullDayLopDeduction;
      const halfDayDeduction = attInfo.halfDayDeduction;
      const attendanceLopDeduction = attInfo.totalAttendanceLopDeduction;
      const workedDays = attInfo.workedDays;

      let pfDeduction = 0;
      if (isEligibleForPF && grossEarnings > 0) {
        pfDeduction = Math.round((grossEarnings * (currentSettings.pfEmployee || 12)) / 100);
      }

      const totalDeductions = pfDeduction + attendanceLopDeduction;
      let netPay = grossEarnings - totalDeductions;
      if (isUnpaid || isDeferred) {
        netPay = 0;
      }

      payrollsToUpdate.push({
        id: r.id,
        data: {
          employeeName: user?.displayName || user?.name || r.employeeName || "Employee",
          employeeId: user?.employeeId || r.employeeId || "—",
          department: user?.department || r.department || "Engineering",
          role: user?.designation || user?.role || r.role || "Staff",
          category,
          baseAmount,
          grossPay: grossEarnings,
          gross: grossEarnings,
          pfDeduction,
          pf: pfDeduction,
          pfEmployer: isEligibleForPF ? Math.round((grossEarnings * (currentSettings.pfEmployer || 12)) / 100) : 0,
          absentDays,
          halfDayCount,
          perDaySalary,
          fullDayLopDeduction,
          halfDayDeduction,
          attendanceLopDeduction,
          deductions: totalDeductions,
          netPay: Math.max(0, netPay),
          net: Math.max(0, netPay),
          presentDays,
          leaveDays,
          workedDays: r.workedDays ?? workedDays,
          updatedAt: serverTimestamp(),
        },
      });
    });

    for (let i = 0; i < payrollsToUpdate.length; i += CHUNK_SIZE) {
      const chunk = payrollsToUpdate.slice(i, i + CHUNK_SIZE);
      const batch = writeBatch(db);
      chunk.forEach((item) => {
        batch.update(doc(db, "payrolls", item.id), item.data);
      });
      await batch.commit();
      updatedCount += chunk.length;
    }

    return { success: true, updatedCount };
  } catch (error) {
    console.error("Error recalculating payrolls with new settings:", error);
    return { success: false, error: error.message, updatedCount };
  }
};

// Update payroll settings and automatically recalculate generated payrolls
export const updatePayrollSettings = async (newSettings) => {
  try {
    const docRef = doc(db, "settings", "payroll");
    await setDoc(docRef, newSettings, { merge: true });

    // Automatically recalculate generated payrolls for all employees across the system
    const syncResult = await recalculateAllGeneratedPayrolls(newSettings);

    return {
      success: true,
      updatedCount: syncResult.updatedCount || 0,
    };
  } catch (error) {
    console.error("Error updating payroll settings:", error);
    return {
      success: false,
      error: error.message,
      updatedCount: 0,
    };
  }
};

// Generate Payroll for a specific month and year
export const generateMonthlyPayroll = async (month, year, users, attendanceData, leavesData, settings) => {
  const currentSettings = settings || (await getPayrollSettings());
  const generatedRecords = [];

  for (const user of users) {
    let category = "Full-Time Employee";
    let baseAmount = Number(user.fullTimeSalary || user.baseSalary || user.salary || currentSettings.fullTimeSalary || 30000);
    let isEligibleForPF = true;
    let isDeferred = false;
    let isUnpaid = false;

    const empType = (user.payrollCategory || user.employmentType || user.category || "full-time").toString().toLowerCase();

    if (empType === "paid-intern" || empType === "fresher-intern") {
      category = "Paid Intern - Fresher";
      baseAmount = Number(user.stipend || user.salary || currentSettings.fresherStipend || 8000);
      isEligibleForPF = false;
    } else if (empType === "paid-intern-experienced" || empType === "experienced-intern") {
      category = "Paid Intern - Experienced";
      baseAmount = Number(user.stipend || user.salary || currentSettings.experiencedStipend || 15000);
      isEligibleForPF = false;
    } else if (empType === "unpaid-intern" || empType === "unpaid") {
      category = "Unpaid Intern";
      baseAmount = 0;
      isEligibleForPF = false;
      isUnpaid = true;
    } else if (empType === "deferred-intern" || empType === "deferred") {
      category = "Deferred Stipend Intern";
      baseAmount = Number(user.stipend || user.salary || currentSettings.deferredStipend || 8000);
      isEligibleForPF = false;
      isDeferred = true;
    }

    const grossEarnings = baseAmount;
    const salaryDays = 30;

    // Calculate Comprehensive Attendance & LOP Deductions
    const attInfo = calculateEmployeeAttendanceAndLop({
      employeeId: user.uid,
      candidateIds: [user.uid, user.id, user.employeeId],
      month,
      year,
      monthlyGross: grossEarnings,
      salaryDays,
      attendanceList: attendanceData || [],
      leavesList: leavesData || [],
    });

    const presentDays = attInfo.presentDays;
    const leaveDays = attInfo.paidLeaveDays;
    const absentDays = attInfo.absentDays;
    const halfDayCount = attInfo.halfDayCount;
    const perDaySalary = attInfo.perDaySalary;
    const fullDayLopDeduction = attInfo.fullDayLopDeduction;
    const halfDayDeduction = attInfo.halfDayDeduction;
    const attendanceLopDeduction = attInfo.totalAttendanceLopDeduction;
    const workedDays = attInfo.workedDays;

    let pfDeduction = 0;
    if (isEligibleForPF && grossEarnings > 0) {
      pfDeduction = Math.round((grossEarnings * (currentSettings.pfEmployee || 12)) / 100);
    }

    const totalDeductions = pfDeduction + attendanceLopDeduction;
    let netPay = grossEarnings - totalDeductions;
    if (isUnpaid || isDeferred) {
      netPay = 0;
    }

    const payrollRecord = {
      userId: user.uid,
      employeeName: user.displayName || user.name || "Employee",
      employeeId: user.employeeId || "—",
      department: user.department || "Engineering",
      role: user.designation || user.role || "Staff",
      category,
      month,
      year,
      baseAmount,
      grossPay: grossEarnings,
      gross: grossEarnings,
      pfDeduction,
      pf: pfDeduction,
      pfEmployer: isEligibleForPF ? Math.round((grossEarnings * (currentSettings.pfEmployer || 12)) / 100) : 0,
      absentDays,
      halfDayCount,
      perDaySalary,
      fullDayLopDeduction,
      halfDayDeduction,
      attendanceLopDeduction,
      deductions: totalDeductions,
      netPay: Math.max(0, netPay),
      net: Math.max(0, netPay),
      presentDays,
      leaveDays,
      workedDays,
      status: isDeferred ? "Deferred" : "Generated",
      generatedAt: new Date().toISOString(),
    };

    generatedRecords.push(payrollRecord);
  }

  // Save to Firestore
  try {
    for (const record of generatedRecords) {
      const q = query(
        collection(db, "payrolls"),
        where("userId", "==", record.userId),
        where("month", "==", month),
        where("year", "==", year)
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        await addDoc(collection(db, "payrolls"), record);
      } else {
        const docId = querySnapshot.docs[0].id;
        await updateDoc(doc(db, "payrolls", docId), record);
      }
    }
    return true;
  } catch (error) {
    console.error("Error generating payroll:", error);
    return false;
  }
};

export const getPayrollsByMonth = async (month, year) => {
  const q = query(collection(db, "payrolls"), where("month", "==", month), where("year", "==", year));
  const snap = await getDocs(q);
  return snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
};

export const getUserPayrolls = async (userId) => {
  const q = query(collection(db, "payrolls"), where("userId", "==", userId));
  const snap = await getDocs(q);
  return snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
};
